/**
 * Light / dark mode.
 *
 * The choice is remembered per user on this device: a clerk who prefers
 * dark keeps it, while the owner signing in on the same PC still gets
 * theirs. Before anyone signs in, the last mode used on the device applies,
 * so the login screen does not flash the wrong theme.
 *
 * The mode is written to <html data-theme>, which switches every CSS
 * variable in index.css; App rebuilds the MUI theme from the same value.
 * index.html applies the stored mode before React loads, for the same
 * no-flash reason.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import type { ThemeMode } from '../theme'

/** Last mode used on this device. index.html reads the same key. */
export const DEVICE_KEY = 'bv.theme'

export function userKey(username: string): string {
  return `${DEVICE_KEY}.${username}`
}

function read(key: string): ThemeMode | null {
  try {
    const v = localStorage.getItem(key)
    return v === 'dark' || v === 'light' ? v : null
  } catch {
    return null
  }
}

function write(key: string, v: ThemeMode) {
  try { localStorage.setItem(key, v) } catch { /* private mode: not remembered */ }
}

interface ThemeModeContext {
  mode: ThemeMode
  setMode: (m: ThemeMode) => void
  toggle: () => void
  /** Tell the provider who is signed in (null when signed out). */
  bindUser: (username: string | null) => void
}

const Ctx = createContext<ThemeModeContext>({
  mode: 'light',
  setMode: () => {},
  toggle: () => {},
  bindUser: () => {},
})

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => read(DEVICE_KEY) ?? 'light')
  const user = useRef<string | null>(null)

  useEffect(() => {
    document.documentElement.dataset.theme = mode
  }, [mode])

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m)
    write(DEVICE_KEY, m)
    if (user.current) write(userKey(user.current), m)
  }, [])

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark'
      write(DEVICE_KEY, next)
      if (user.current) write(userKey(user.current), next)
      return next
    })
  }, [])

  const bindUser = useCallback((username: string | null) => {
    user.current = username
    if (!username) return
    const saved = read(userKey(username))
    if (saved) {
      setModeState(saved)
      write(DEVICE_KEY, saved)
    }
  }, [])

  const value = useMemo(
    () => ({ mode, setMode, toggle, bindUser }), [mode, setMode, toggle, bindUser])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useThemeMode(): ThemeModeContext {
  return useContext(Ctx)
}
